import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Crop, ArrowsInSimple, ArrowClockwise } from "@phosphor-icons/react";

/**
 * ImageCropperModal — interactive crop + zoom + rotate before upload.
 *
 * Props:
 *   open        boolean            — dialog visibility
 *   src         string (dataURL)   — the image to crop
 *   aspect      number             — width / height ratio (e.g. 1 for square, 3 for banner)
 *   onCancel    () => void
 *   onConfirm   (blob: Blob, previewUrl: string) => void — receives the cropped image
 */
export default function ImageCropperModal({ open, src, aspect = 1, onCancel, onConfirm, label = "Adjust image" }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedArea, setCroppedArea] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedArea(croppedPixels);
  }, []);

  const reset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  const confirm = async () => {
    if (!src || !croppedArea) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(src, croppedArea, rotation);
      const previewUrl = URL.createObjectURL(blob);
      onConfirm(blob, previewUrl);
    } catch (err) {
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-2xl" data-testid="image-cropper-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold flex items-center gap-2">
            <Crop size={20} weight="fill" className="text-emerald-600" /> {label}
          </DialogTitle>
          <DialogDescription>
            Drag to reposition, use the slider to zoom, and rotate if needed. The framed area is exactly how the image will appear on the site.
          </DialogDescription>
        </DialogHeader>

        <div className="relative w-full aspect-video bg-slate-900 rounded-xl overflow-hidden" data-testid="cropper-canvas">
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              showGrid={true}
              restrictPosition={true}
            />
          )}
        </div>

        <div className="space-y-3 pt-1">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <ArrowsInSimple size={12} /> Zoom
              </span>
              <span className="text-xs text-muted-foreground num-display">{zoom.toFixed(2)}×</span>
            </div>
            <Slider
              min={1}
              max={4}
              step={0.05}
              value={[zoom]}
              onValueChange={(v) => setZoom(v[0])}
              data-testid="cropper-zoom-slider"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <ArrowClockwise size={12} /> Rotate
              </span>
              <span className="text-xs text-muted-foreground num-display">{rotation}°</span>
            </div>
            <Slider
              min={-180}
              max={180}
              step={1}
              value={[rotation]}
              onValueChange={(v) => setRotation(v[0])}
              data-testid="cropper-rotate-slider"
            />
          </div>
          <button
            type="button"
            onClick={reset}
            className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground hover:text-foreground"
            data-testid="cropper-reset-btn"
          >
            Reset adjustments
          </button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="cropper-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={confirm}
            disabled={busy || !croppedArea}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="cropper-confirm-btn"
          >
            {busy ? "Processing…" : "Save & upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- Client-side canvas crop --------
async function getCroppedBlob(imageSrc, pixelCrop, rotation = 0) {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const rotRad = (rotation * Math.PI) / 180;
  const { width: bBoxWidth, height: bBoxHeight } = rotatedBoundingBox(image.width, image.height, rotation);

  // Temporary large canvas that fits the rotated image
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);

  // Now extract the crop from the rotated canvas
  const data = ctx.getImageData(pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height);
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.putImageData(data, 0, 0);

  // Downscale extremely large crops to keep file size sane (max ~2000px on longest side)
  const MAX_DIM = 2000;
  let finalWidth = canvas.width;
  let finalHeight = canvas.height;
  if (Math.max(finalWidth, finalHeight) > MAX_DIM) {
    const scale = MAX_DIM / Math.max(finalWidth, finalHeight);
    const scaled = document.createElement("canvas");
    scaled.width = Math.round(finalWidth * scale);
    scaled.height = Math.round(finalHeight * scale);
    const sctx = scaled.getContext("2d");
    sctx.imageSmoothingQuality = "high";
    sctx.drawImage(canvas, 0, 0, scaled.width, scaled.height);
    return new Promise((resolve) => scaled.toBlob((b) => resolve(b), "image/jpeg", 0.9));
  }
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92));
}

function rotatedBoundingBox(width, height, rotation) {
  const rotRad = (rotation * Math.PI) / 180;
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
